/**
 * Order page state — the order + its events (polled every 15 s), the live track payload (status step, shop / rider /
 * customer pins, contacts, support), the review state, the return request, cancel, and return submission. Moved
 * verbatim out of OrderTrackPage so the classic view and the Blinkit view share one implementation.
 *
 * ``deriveOrderView`` is the page's display logic (status label/colour with the stale-track fallback, rider status,
 * the return window, whether a review is blocked by a return) — also moved verbatim, so both views agree.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { useTenant } from "../../../contexts/TenantContext";

export function useOrderTrackController() {
    const { id } = useParams();
    const { tenant } = useTenant();
    const [data, setData] = useState(null);
    const [track, setTrack] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [showReview, setShowReview] = useState(false);
    const [existingReview, setExistingReview] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const [returnRequest, setReturnRequest] = useState(null);
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [returnReason, setReturnReason] = useState("");
    const [submittingReturn, setSubmittingReturn] = useState(false);

    // Read only by the Blinkit view: why the order couldn't be loaded ("missing" = 404, else "failed").
    const [loadError, setLoadError] = useState(null);
    const loadOrder = async () => {
        try {
            const { data } = await api.get(`/orders/${id}`);
            setData(data);
            setLoadError(null);
        } catch (err) {
            console.error("[OrderTrack] loadOrder failed:", err);
            setLoadError(err?.response?.status === 404 ? "missing" : "failed");
        }
    };
    const loadTrack = async () => {
        setRefreshing(true);
        try {
            const { data } = await api.get(`/orders/${id}/track`);
            setTrack(data);
        } catch (err) { console.error("[OrderTrack] loadTrack failed:", err); } finally { setRefreshing(false); }
    };
    const loadReviewState = async () => {
        try {
            const { data } = await api.get(`/orders/${id}/review`);
            setExistingReview(data?.review || null);
        } catch (err) { console.debug("[OrderTrack] loadReviewState (404 is OK for unreviewed orders):", err); }
    };
    const loadReturnState = async () => {
        try {
            const { data } = await api.get(`/orders/${id}/return`);
            setReturnRequest(data || null);
        } catch (err) { setReturnRequest(null); /* 404 is OK — no return requested yet */ }
    };
    const submitReturnRequest = async () => {
        if (!returnReason.trim() || returnReason.trim().length < 3) {
            toast.error("Please describe the issue (a few words is fine)");
            return;
        }
        setSubmittingReturn(true);
        try {
            const { data } = await api.post(`/orders/${id}/return-request`, { reason: returnReason.trim() });
            setReturnRequest(data);
            setShowReturnModal(false);
            setReturnReason("");
            toast.success("Return requested — we'll review it shortly.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not request a return");
        } finally { setSubmittingReturn(false); }
    };

    const cancelOrder = async () => {
        const reason = window.prompt("Why are you cancelling? (optional)") || "Customer cancelled";
        if (!window.confirm("Cancel this order? Refund will post to your wallet immediately.")) return;
        setCancelling(true);
        try {
            const { data: out } = await api.post(`/orders/${id}/cancel?reason=${encodeURIComponent(reason)}`);
            const refund = out?.refund?.amount;
            toast.success(refund ? `Cancelled. ₹${refund} refunded to wallet.` : "Order cancelled.");
            loadOrder();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not cancel — try again");
        } finally { setCancelling(false); }
    };

    useEffect(() => {
        let cancelled = false;
        const fetchAll = async () => {
            if (cancelled) return;
            await loadOrder();
            if (cancelled) return;
            await loadTrack();
            if (cancelled) return;
            await loadReviewState();
            if (cancelled) return;
            await loadReturnState();
        };
        fetchAll();
        // Also re-poll return state on the same tick — otherwise an
        // admin approving/rejecting a return while the customer has this
        // page open never updates the pill without a manual reload.
        const t = setInterval(() => {
            if (cancelled) return;
            loadOrder();
            loadReturnState();
        }, 15000);
        return () => { cancelled = true; clearInterval(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // When the polled order doc transitions to a new status, refetch the
    // track payload too — otherwise ``track.order.current_step.label``
    // stays frozen at whatever it was on initial load (e.g. "Placed")
    // even after the order is marked DELIVERED, which causes a
    // misleading status pill at the top of the page.
    const orderStatus = data?.order?.status;
    useEffect(() => {
        if (!orderStatus) return;
        // Defer to a microtask so the lint rule about setState-in-effect
        // is satisfied (network handlers schedule state updates outside
        // the synchronous effect tick).
        Promise.resolve().then(() => {
            loadTrack();
            if (orderStatus === "DELIVERED") {
                loadReviewState();
                loadReturnState();
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderStatus]);

    const points = useMemo(() => {
        if (!track) return [];
        const out = [];
        if (track.shop?.lat && track.shop?.lng) out.push([track.shop.lat, track.shop.lng]);
        if (track.rider?.lat && track.rider?.lng) out.push([track.rider.lat, track.rider.lng]);
        if (track.customer?.lat && track.customer?.lng) out.push([track.customer.lat, track.customer.lng]);
        return out;
    }, [track]);

    return {
        id, tenant, data, loadError, track, refreshing, showReview, setShowReview, existingReview, cancelling, returnRequest,
        showReturnModal, setShowReturnModal, returnReason, setReturnReason, submittingReturn, loadOrder, loadTrack,
        loadReviewState, loadReturnState, submitReturnRequest, cancelOrder, points,
    };
}

export const STATUS_FALLBACK = {
    PLACED: { label: "Placed", color: "#f59e0b" },
    CONFIRMED: { label: "Confirmed", color: "#3b82f6" },
    PREPARING: { label: "Preparing", color: "#3b82f6" },
    READY: { label: "Ready for pickup", color: "#3b82f6" },
    PICKED_UP: { label: "Picked up", color: "#6366f1" },
    OUT_FOR_DELIVERY: { label: "Out for delivery", color: "#6366f1" },
    DELIVERED: { label: "Delivered", color: "#10b981" },
    CANCELLED: { label: "Cancelled", color: "#ef4444" },
    REJECTED: { label: "Rejected", color: "#ef4444" },
};

export function deriveOrderView({ data, track, tenant, returnRequest, points }) {
    const o = data.order;
    const events = data.events || [];
    const status = o.status;
    // Return-window eligibility, computed the same way the backend does
    // (delivered_at + tenant.return_window_days, default 7) so the
    // "Request a return" button doesn't stay visible indefinitely after
    // the window has actually closed — the backend still enforces this
    // for real on submit, this is purely a display/UX pre-check.
    const returnWindowDays = Number(tenant?.return_window_days) || 7;
    const deliveredAtMs = o.delivered_at ? new Date(o.delivered_at).getTime() : null;
    const returnWindowOpen = !!deliveredAtMs &&
        Date.now() <= deliveredAtMs + returnWindowDays * 24 * 60 * 60 * 1000;
    // An active or completed return means the item is in dispute or was
    // already refunded — mirrors the backend's submit_review guard
    // (reviews.py). A REJECTED return does NOT block review: the
    // customer kept the item, nothing was refunded, so a normal review
    // is legitimate.
    const reviewBlockedByReturn = !!returnRequest && ["PENDING", "APPROVED"].includes(returnRequest.status);
    // Client-side fallback so the status pill ALWAYS reflects the most
    // recent ``o.status`` from /orders/:id (which polls every 15s),
    // even if the /track payload is stale or hasn't returned yet.
    // The map is intentionally small — only the cases where colour /
    // label clearly matter to the customer. For everything else we
    // fall back to the workflow step shipped by /track.
    const fb = STATUS_FALLBACK[status] || { label: status, color: "#6366f1" };
    // Trust the track payload only when its slug matches the current
    // order status — otherwise it's stale and we use the fallback.
    const trackStep = track?.order?.current_step;
    const useTrack = trackStep && trackStep.slug === status;
    const stepLabel = useTrack ? trackStep.label : fb.label;
    const stepColor = useTrack ? trackStep.color : fb.color;
    // Parallel rider tracker — distinct from the shop's prep status.
    // We render it as a secondary pill so the customer can see "Rider
    // on the way" while the shop is still PREPARING.
    const RIDER_STATUS_LABEL = {
        ASSIGNED: { label: "Rider assigned", color: "#8b5cf6" },
        PICKED_UP: { label: "Rider picked up", color: "#6366f1" },
        OUT_FOR_DELIVERY: { label: "Rider on the way", color: "#6366f1" },
        DELIVERED: { label: "Delivered", color: "#10b981" },
    };
    const riderStatus = o.rider_status ? RIDER_STATUS_LABEL[o.rider_status] : null;
    const hasMap = points.length >= 1;

    return {
        o, events, status, returnWindowDays, returnWindowOpen, reviewBlockedByReturn, stepLabel, stepColor, riderStatus,
        hasMap,
    };
}
